import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Mail, Clock, CheckCircle, Circle, X, User, ChevronDown, Pin, PinOff, Filter } from 'lucide-react';
import { LearnerInlineContentLoader } from './shared/LearnerInlineContentLoader';
import { messageService, UserMessageDto, type InboxFilterState, inboxFilterKey, applyInboxFiltersClient, messageIsRead } from '../services/messageService';
import { useTimezone } from '../hooks/useTimezone';
import { useTheme } from '../hooks/useTheme';
import { useNotifications } from './feedback/NotificationSystem';
import { describeInboxFilters, InboxFiltersPanel, countActiveInboxFilters } from './InboxFiltersPanel';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../hooks/useLocale';
import { formatAppDateTime } from '../utils/localeDateFormat';
import { resolveStoredMessage } from '../utils/localizeStoredMessage';
import { useLearnerSlideModal } from '../hooks/useLearnerSlideModal';
import { readPersistedInboxFilters, writePersistedInboxFilters } from '../utils/learnerFilterPersistence';
import { useLearnerHomeDesign } from '../utils/learnerHomeDesign';
import { simpleFilterToggleActiveClass, simpleFilterToggleCountClass, simpleFilterToggleIdleClass } from './shared/simpleFilterChrome';

const INBOX_PINNED_IDS_KEY = 'persianInboxPinnedMessageIds';
const MAX_PINNED_MESSAGES = 5;

// Legacy animation classes kept for any external references
if (typeof document !== 'undefined' && !document.getElementById('inbox-modal-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'inbox-modal-styles';
  styleSheet.textContent = `
    .message-modal-content {
      min-width: 320px;
      width: auto;
    }
  `;
  document.head.appendChild(styleSheet);
}

interface InboxProps {
  className?: string;
  onMessageRead?: () => void;
  isAdminView?: boolean; // If true, show real username instead of senderName
  /** When false, the filter panel starts collapsed (e.g. profile Messages tab). */
  defaultFiltersExpanded?: boolean;
}

const Inbox: React.FC<InboxProps> = ({
  className = '',
  onMessageRead,
  isAdminView = false,
  defaultFiltersExpanded = true
}) => {
  const [messages, setMessages] = useState<UserMessageDto[]>([]);
  const [selectedMessage, setSelectedMessage] = useState<UserMessageDto | null>(null);
  const [displayMessage, setDisplayMessage] = useState<UserMessageDto | null>(null); // Keep message during closing animation
  const [highlightedMessageId, setHighlightedMessageId] = useState<number | null>(null); // Separate state for highlighting to prevent re-renders
  const [selectedMessageIds, setSelectedMessageIds] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [inboxFilters, setInboxFilters] = useState<InboxFilterState>(() => readPersistedInboxFilters());
  const [filtersOpen, setFiltersOpen] = useState(defaultFiltersExpanded);
  const inboxActiveFilterCount = countActiveInboxFilters(inboxFilters);
  const inboxFiltersRef = useRef(inboxFilters);
  inboxFiltersRef.current = inboxFilters;
  const { formatToLocalTime, timezoneInfo } = useTimezone();
  const { isDark } = useTheme();
  const { showWarning } = useNotifications();
  const { t } = useTranslation(['messages', 'common']);
  const { locale, direction } = useLocale();
  const isRtl = direction === 'rtl';
  const [homeDesign] = useLearnerHomeDesign();
  const simpleHome = homeDesign === 'mme';

  const [pinnedIds, setPinnedIds] = useState<number[]>([]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(INBOX_PINNED_IDS_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as unknown;
      if (!Array.isArray(parsed)) return;
      const next = parsed
        .map((x) => (typeof x === 'number' ? x : parseInt(String(x), 10)))
        .filter((x) => !Number.isNaN(x))
        .slice(0, MAX_PINNED_MESSAGES);
      setPinnedIds(next);
    } catch {
      /* ignore */
    }
  }, []);

  const persistPinnedIds = useCallback((ids: number[]) => {
    const next = ids.slice(0, MAX_PINNED_MESSAGES);
    setPinnedIds(next);
    try {
      localStorage.setItem(INBOX_PINNED_IDS_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  }, []);

  const togglePin = useCallback(
    (e: React.MouseEvent, messageId: number) => {
      e.stopPropagation();
      if (pinnedIds.includes(messageId)) {
        persistPinnedIds(pinnedIds.filter((id) => id !== messageId));
        return;
      }
      if (pinnedIds.length >= MAX_PINNED_MESSAGES) {
        showWarning(t('pinMaxWarning', { max: MAX_PINNED_MESSAGES }));
        return;
      }
      persistPinnedIds([...pinnedIds, messageId]);
    },
    [pinnedIds, persistPinnedIds, showWarning]
  );

  useEffect(() => {
    if (messages.length === 0) return;
    const valid = new Set(messages.map((m) => m.id));
    setPinnedIds((prev) => {
      const filtered = prev.filter((id) => valid.has(id));
      if (filtered.length === prev.length) return prev;
      try {
        localStorage.setItem(INBOX_PINNED_IDS_KEY, JSON.stringify(filtered));
      } catch {
        /* ignore */
      }
      return filtered;
    });
  }, [messages]);

  const orderedMessages = useMemo(() => {
    const pinSet = new Set(pinnedIds);
    const pinned = pinnedIds
      .map((id) => messages.find((m) => m.id === id))
      .filter((m): m is UserMessageDto => Boolean(m));
    const rest = messages.filter((m) => !pinSet.has(m.id));
    return [...pinned, ...rest];
  }, [messages, pinnedIds]);

  const pinnedInView = useMemo(
    () =>
      pinnedIds
        .map((id) => messages.find((m) => m.id === id))
        .filter((m): m is UserMessageDto => Boolean(m)),
    [messages, pinnedIds]
  );

  const setReadFilter = useCallback((read: InboxFilterState['read']) => {
    setInboxFilters((prev) => (prev.read === read ? prev : { ...prev, read }));
  }, []);

  const setDateFilter = useCallback((date: InboxFilterState['date']) => {
    setInboxFilters((prev) => (prev.date === date ? prev : { ...prev, date }));
  }, []);

  useEffect(() => {
    writePersistedInboxFilters(inboxFilters);
  }, [inboxFilters]);

  const filterListKey = inboxFilterKey(inboxFilters);

  const handleMessageModalClose = useCallback(() => {
    setSelectedMessage(null);
    setDisplayMessage(null);
    setHighlightedMessageId(null);
  }, []);

  const messageModalOpen = selectedMessage !== null;
  const {
    mounted: messageModalMounted,
    visible: messageModalVisible,
    panelRef: messageModalPanelRef,
    requestClose: requestMessageModalClose,
    handleExitTransitionEnd: handleMessageModalExitTransitionEnd,
  } = useLearnerSlideModal(messageModalOpen, handleMessageModalClose, undefined, { lockScroll: false });

  // Initial load: show a full-card loader until the first page arrives (remount uses key from profile).
  useEffect(() => {
    void loadMessages(true, inboxFilters);
  }, []);

  // Reload when either filter dimension changes (skip initial mount — handled above).
  const filterMountedRef = useRef(false);
  useEffect(() => {
    if (!filterMountedRef.current) {
      filterMountedRef.current = true;
      return;
    }
    setSelectedMessageIds(new Set());
    setIsFilterLoading(true);
    void loadMessages(false, inboxFilters, { filterOnly: true });
  }, [inboxFilters.read, inboxFilters.date]);

  // Listen for manual refresh events (don't show loader on refresh)
  useEffect(() => {
    const handleRefresh = () => {
      setMessages([]);
      loadMessages(false, inboxFiltersRef.current);
    };
    window.addEventListener('refreshInbox', handleRefresh);
    return () => {
      window.removeEventListener('refreshInbox', handleRefresh);
    };
  }, []);

  const loadMessages = async (
    showLoader = false,
    filters: InboxFilterState = inboxFiltersRef.current,
    options?: { filterOnly?: boolean }
  ) => {
    try {
      if (options?.filterOnly) {
        setIsFilterLoading(true);
      } else if (showLoader || messages.length === 0) {
        setIsLoading(true);
      }
      setError(null);
      // Try paginated endpoint first
      try {
        const response = await messageService.getUserMessagesPaginated(0, 10, filters);
        console.log('Paginated response:', response);
        
        // Handle different possible response structures
        let messagesArray: UserMessageDto[] = [];
        let total = 0;
        
        if (response) {
          // Check if messages is directly in response
          if (Array.isArray(response.messages)) {
            messagesArray = response.messages;
            total = response.totalCount || response.messages.length;
          }
          // Check if response is wrapped in data property
          else if (response.data && Array.isArray(response.data.messages)) {
            messagesArray = response.data.messages;
            total = response.data.totalCount || response.data.messages.length;
          }
          // Check if response itself is an array (fallback)
          else if (Array.isArray(response)) {
            messagesArray = response;
            total = response.length;
          }
        }
        
        if (messagesArray.length >= 0) { // Allow empty arrays
          setMessages(messagesArray);
          setTotalCount(total);
          setSelectedMessageIds(new Set()); // Clear selection on reload
          return;
        } else {
          console.warn('Unexpected response format:', response);
          throw new Error('Invalid response format');
        }
      } catch (paginatedError) {
        console.warn('Paginated endpoint failed, falling back to non-paginated:', paginatedError);
        // Fallback to non-paginated endpoint if paginated fails
        try {
          const allMessages = await messageService.getUserMessages();
          console.log('Non-paginated messages:', allMessages);
          if (Array.isArray(allMessages)) {
            const sortedData = [...allMessages].sort((a, b) => 
              new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime()
            );
            const filtered = applyInboxFiltersClient(sortedData, filters);
            setMessages(filtered.slice(0, 10));
            setTotalCount(filtered.length);
            setSelectedMessageIds(new Set());
            return;
          } else {
            throw new Error('Non-paginated endpoint returned invalid data');
          }
        } catch (fallbackError) {
          console.error('Fallback endpoint also failed:', fallbackError);
          throw fallbackError; // Re-throw to be caught by outer catch
        }
      }
    } catch (err: any) {
      console.error('Failed to load messages:', err);
      const errorMessage = err?.message || t('loadFailed');
      setError(errorMessage);
      // Ensure messages array is set to empty on error
      setMessages([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
      setIsFilterLoading(false);
    }
  };

  const loadMoreMessages = async () => {
    if (isLoadingMore || messages.length >= totalCount) return;
    
    try {
      setIsLoadingMore(true);
      setError(null);
      const response = await messageService.getUserMessagesPaginated(messages.length, 10, inboxFilters);
      // Append new messages to existing ones
      setMessages(prev => [...prev, ...response.messages]);
      setTotalCount(response.totalCount);
    } catch (err) {
      console.error('Failed to load more messages:', err);
      setError(t('loadMoreFailed'));
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleMessageClick = async (message: UserMessageDto, event?: React.MouseEvent) => {
    // Don't open modal if clicking checkbox
    if (event && (event.target as HTMLElement).closest('input[type="checkbox"]')) {
      return;
    }
    if (event && (event.target as HTMLElement).closest('[data-pin-control]')) {
      return;
    }
    
    setSelectedMessage(message);
    setDisplayMessage(message); // Set display message for animation
    setHighlightedMessageId(message.id); // Set highlighted ID
    
    // ALWAYS mark as read when opening message (regardless of current state)
    try {
      await messageService.markAsRead(message.id);
      
      // Update only the specific message in the list (don't reload all messages to prevent flash)
      setMessages(prevMessages => 
        prevMessages.map(m => 
          m.id === message.id 
            ? { ...m, isRead: true, readAt: new Date().toISOString() }
            : m
        )
      );
      
      // Update selected message with read status
      const updatedMessage = { ...message, isRead: true, readAt: new Date().toISOString() };
      setSelectedMessage(updatedMessage);
      setDisplayMessage(updatedMessage);
      
      // Refresh badge count immediately
      if (onMessageRead) {
        onMessageRead();
      }
      window.dispatchEvent(new CustomEvent('messageRead'));
      // Second refresh to ensure sync
      setTimeout(() => {
        if (onMessageRead) {
          onMessageRead();
        }
        window.dispatchEvent(new CustomEvent('messageRead'));
      }, 500);
    } catch (err) {
      console.error('Failed to mark message as read:', err);
    }
  };

  const handleSelectMessage = (messageId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedMessageIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedMessageIds(new Set(messages.map(m => m.id)));
    } else {
      setSelectedMessageIds(new Set());
    }
  };

  const handleBulkMarkAsRead = async () => {
    if (selectedMessageIds.size === 0) return;
    
    setIsProcessing(true);
    try {
      const promises = Array.from(selectedMessageIds).map(id => messageService.markAsRead(id));
      await Promise.all(promises);
      
      setSelectedMessageIds(new Set());
      
      // Reload first page of messages to get latest state from DB
      // Reset to first 10 messages after bulk operation
      setMessages([]);
      await loadMessages(false, inboxFiltersRef.current);
      
      // Refresh badge count after API calls complete
      setTimeout(() => {
        if (onMessageRead) {
          onMessageRead();
        }
        window.dispatchEvent(new CustomEvent('messageRead'));
      }, 200);
      // Second refresh to ensure backend state is updated
      setTimeout(() => {
        if (onMessageRead) {
          onMessageRead();
        }
        window.dispatchEvent(new CustomEvent('messageRead'));
      }, 800);
    } catch (err) {
      console.error('Failed to mark messages as read:', err);
      setError(t('updateFailed'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBulkMarkAsUnread = async () => {
    if (selectedMessageIds.size === 0) return;
    
    setIsProcessing(true);
    try {
      const promises = Array.from(selectedMessageIds).map(id => messageService.markAsUnread(id));
      await Promise.all(promises);
      
      setSelectedMessageIds(new Set());
      
      // Reload first page of messages to get latest state from DB
      // Reset to first 10 messages after bulk operation
      setMessages([]);
      await loadMessages(false, inboxFiltersRef.current);
      
      // Refresh badge count after API calls complete
      setTimeout(() => {
        if (onMessageRead) {
          onMessageRead();
        }
        window.dispatchEvent(new CustomEvent('messageRead'));
      }, 200);
      // Second refresh to ensure backend state is updated
      setTimeout(() => {
        if (onMessageRead) {
          onMessageRead();
        }
        window.dispatchEvent(new CustomEvent('messageRead'));
      }, 800);
    } catch (err) {
      console.error('Failed to mark messages as unread:', err);
      setError(t('updateFailed'));
    } finally {
      setIsProcessing(false);
    }
  };

  const formatReceiveTime = (dateString: string): string => {
    try {
      if (locale === 'fa') {
        return formatAppDateTime(dateString, locale, timezoneInfo?.timeZoneId);
      }
      return formatToLocalTime(dateString, 'datetime');
    } catch {
      return dateString;
    }
  };

  const localizedDisplayMessage = useMemo(() => {
    if (!displayMessage) return null;
    const localized = resolveStoredMessage(displayMessage, locale, t, isRtl);
    return { ...displayMessage, subject: localized.subject, content: localized.content };
  }, [displayMessage, locale, t, isRtl]);

  const resolveMessagePreview = useCallback(
    (message: UserMessageDto) => resolveStoredMessage(message, locale, t, isRtl).subject,
    [locale, t, isRtl]
  );

  const getSenderDisplay = (message: UserMessageDto): string => {
    if (isAdminView) {
      return message.senderUsername || message.sentBy || t('systemSender');
    } else {
      return message.senderName || message.sentBy || t('systemSender');
    }
  };

  const handleCloseModal = () => {
    requestMessageModalClose();
  };

  const emptyFilterLabel = describeInboxFilters(inboxFilters, t);

  const allSelected = messages.length > 0 && selectedMessageIds.size === messages.length;
  const someSelected = selectedMessageIds.size > 0 && selectedMessageIds.size < messages.length;

  // Only show full-card loader before we have any rows (avoids flashing empty state while the request is in flight).
  if (isLoading && messages.length === 0 && !error) {
    return (
      <div className={`bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700 ${className}`}>
        <LearnerInlineContentLoader message={t('loading')} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700 ${className}`}>
        <div className="text-center py-8">
          <p className="text-red-600 dark:text-red-400">{error}</p>
          <button
            onClick={() => loadMessages(true)}
            className="mt-4 px-4 py-2 bg-purple-600 dark:bg-purple-500 text-white rounded-lg hover:bg-purple-700 dark:hover:bg-purple-600 transition-colors"
          >
            {t('retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      dir={direction}
      className={`bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700 ${className}`}
    >
      {/* Header with gradient background */}
      <div className="mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            <div className="p-1.5 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 dark:from-purple-600 dark:to-pink-600 shrink-0">
              <Mail className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <h3 className="truncate text-base sm:text-lg font-bold text-gray-900 dark:text-white">{t('messages:inboxTitle')}</h3>
              <p className="mt-0.5 truncate text-[10px] leading-tight text-gray-500 dark:text-gray-400 sm:text-xs sm:leading-normal">
                {totalCount > 0 ? (
                  <>
                    {t('messages:showingCount', { shown: messages.length, total: totalCount })}
                  </>
                ) : (
                  <>
                    {t('messageCount', { count: messages.length })}
                  </>
                )}
              </p>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {selectedMessageIds.size > 0 && (
              <div className="hidden md:flex items-center gap-2">
                <button
                  onClick={handleBulkMarkAsRead}
                  disabled={isProcessing}
                  className="px-4 py-2 bg-green-500 hover:bg-green-600 dark:bg-green-600 dark:hover:bg-green-700 text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <CheckCircle className="w-4 h-4" />
                  <span>{t('markAsRead')}</span>
                </button>
                <button
                  onClick={handleBulkMarkAsUnread}
                  disabled={isProcessing}
                  className="px-4 py-2 bg-orange-500 hover:bg-orange-600 dark:bg-orange-600 dark:hover:bg-orange-700 text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Circle className="w-4 h-4" />
                  <span>{t('markAsUnread')}</span>
                </button>
              </div>
            )}
            <button
              type="button"
              onClick={() => setFiltersOpen((o) => !o)}
              className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-[11px] font-medium shadow-sm transition-colors duration-200 sm:gap-2 sm:px-3 sm:py-1.5 sm:text-sm ${
                simpleHome
                  ? filtersOpen || inboxActiveFilterCount > 0
                    ? simpleFilterToggleActiveClass
                    : simpleFilterToggleIdleClass
                  : filtersOpen || inboxActiveFilterCount > 0
                    ? 'border-cyan-300/70 bg-cyan-50/95 text-cyan-800 shadow-sm dark:border-cyan-600/55 dark:bg-cyan-950/45 dark:text-cyan-100'
                    : 'border-cyan-200/70 bg-white/90 text-cyan-700 hover:bg-cyan-50/95 dark:border-cyan-700/50 dark:bg-slate-900/50 dark:text-cyan-100'
              }`}
              aria-expanded={filtersOpen}
              aria-label={filtersOpen ? t('collapseFilters') : t('expandFilters')}
            >
              <Filter className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              {t('filters')}
              {inboxActiveFilterCount > 0 ? (
                <span className={simpleHome ? simpleFilterToggleCountClass : 'rounded-full bg-cyan-500 px-1.5 py-0.5 text-[9px] font-bold text-white tabular-nums sm:text-[10px]'}>
                  {inboxActiveFilterCount}
                </span>
              ) : null}
            </button>
          </div>
        </div>
      </div>

      <div
        className={`grid overflow-hidden transition-[grid-template-rows,opacity,margin-bottom] duration-200 ease-out ${
          filtersOpen ? 'mb-5 opacity-100' : 'mb-0 opacity-0'
        }`}
        style={{ gridTemplateRows: filtersOpen ? '1fr' : '0fr' }}
      >
        <div className="min-h-0 overflow-hidden">
          <div
            className={`transition-[transform,opacity] duration-200 ease-out ${
              filtersOpen ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0'
            }`}
          >
            <InboxFiltersPanel
              filters={inboxFilters}
              onReadFilterChange={setReadFilter}
              onDateFilterChange={setDateFilter}
            />
          </div>
        </div>
      </div>

      {selectedMessageIds.size > 0 && (
        <div className="mb-4 flex flex-wrap gap-2 md:hidden">
          <button
            onClick={handleBulkMarkAsRead}
            disabled={isProcessing}
            className="flex-1 min-w-[9rem] px-3 py-2 bg-green-500 hover:bg-green-600 dark:bg-green-600 dark:hover:bg-green-700 text-white rounded-lg transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
          >
            <CheckCircle className="w-3.5 h-3.5" />
            <span>{t('markAsRead')}</span>
          </button>
          <button
            onClick={handleBulkMarkAsUnread}
            disabled={isProcessing}
            className="flex-1 min-w-[9rem] px-3 py-2 bg-orange-500 hover:bg-orange-600 dark:bg-orange-600 dark:hover:bg-orange-700 text-white rounded-lg transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
          >
            <Circle className="w-3.5 h-3.5" />
            <span>{t('markAsUnread')}</span>
          </button>
        </div>
      )}

      <div key={filterListKey} className="min-h-[8rem]">
      {isFilterLoading ? (
        <LearnerInlineContentLoader message={t('loading')} />
      ) : messages.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 flex items-center justify-center">
            <Mail className="w-8 h-8 text-purple-500 dark:text-purple-400" />
          </div>
          <p className="text-gray-600 dark:text-gray-300 text-sm font-medium">
            {isLoading ? t('loading') : emptyFilterLabel ? t('noFilteredMessages', { filter: emptyFilterLabel }) : t('noMessages')}
          </p>
          <p className="text-gray-500 dark:text-gray-400 text-xs mt-2">
            {emptyFilterLabel
              ? t('tryAnotherFilter')
              : t('noMessagesYet')}
          </p>
        </div>
      ) : (
        <>
          {/* Select All Checkbox */}
          <div className="mb-4 flex items-center gap-3 pb-3 border-b border-gray-200 dark:border-gray-700">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(input) => {
                  if (input) {
                    input.indeterminate = someSelected;
                  }
                }}
                onChange={handleSelectAll}
                className="w-5 h-5 text-purple-600 dark:text-purple-500 rounded border-gray-300 dark:border-gray-600 focus:ring-purple-500 dark:focus:ring-purple-400 bg-white dark:bg-gray-700"
              />
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                {t('selectAll', { count: selectedMessageIds.size })}
              </span>
            </label>
          </div>

          {pinnedInView.length > 0 && (
            <div className="mb-4 space-y-2 w-full" dir={direction}>
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide text-start">{t('pinned')}</p>
              <div className="flex w-full flex-wrap gap-2 justify-start">
                {pinnedInView.map((pm) => (
                  <div
                    key={`pin-${pm.id}`}
                    className={`relative max-w-[220px] sm:max-w-xs rounded-lg border border-amber-200 dark:border-amber-700/80 bg-amber-50/90 dark:bg-amber-950/40 px-3 py-2 shadow-sm ${isRtl ? 'ps-3 pe-8' : 'pe-8'}`}
                  >
                    <button
                      type="button"
                      data-pin-control
                      onClick={(e) => togglePin(e, pm.id)}
                      className={`absolute top-2 p-1 rounded-md text-amber-700 dark:text-amber-300 hover:bg-amber-100/80 dark:hover:bg-amber-900/50 ${isRtl ? 'right-2' : 'left-2'}`}
                      title={t('unpinMessage')}
                      aria-label={t('unpinMessage')}
                    >
                      <PinOff className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleMessageClick(pm)}
                      className={`block w-full min-w-0 ${isRtl ? 'text-end pe-7' : 'text-start ps-7'}`}
                    >
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-2">{resolveMessagePreview(pm)}</span>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-3">
            {orderedMessages.map((message) => {
              const isSelected = selectedMessageIds.has(message.id);
              const isPinned = pinnedIds.includes(message.id);
              return (
                <div
                  key={message.id}
                  onClick={(e) => handleMessageClick(message, e)}
                  dir={direction}
                  className={`group relative p-3 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                    messageIsRead(message)
                      ? isDark
                        ? 'bg-gray-700/50 border-gray-600 hover:border-gray-500 hover:bg-gray-700'
                        : 'bg-gradient-to-r from-gray-50 to-gray-100/50 border-gray-200 hover:border-gray-300 hover:shadow-md'
                      : isDark
                        ? 'bg-gradient-to-r from-purple-900/30 to-pink-900/30 border-purple-600 hover:border-purple-500 hover:shadow-lg'
                        : 'bg-gradient-to-r from-purple-50 to-pink-50 border-purple-300 hover:border-purple-400 hover:shadow-lg'
                      } ${
                        highlightedMessageId === message.id
                          ? 'ring-2 ring-purple-500 dark:ring-purple-400'
                          : ''
                      } ${
                    isSelected
                      ? 'ring-2 ring-blue-500 dark:ring-blue-400'
                      : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex flex-col items-center gap-1.5 flex-shrink-0 mt-1">
                      <label
                        onClick={(e) => handleSelectMessage(message.id, e)}
                        className="cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          onClick={(e) => handleSelectMessage(message.id, e)}
                          className="w-5 h-5 text-purple-600 dark:text-purple-400 rounded border-gray-300 dark:border-gray-600 focus:ring-purple-500 dark:focus:ring-purple-400 bg-white dark:bg-gray-700 cursor-pointer"
                          aria-label={`Select message: ${message.subject}`}
                        />
                      </label>
                      <button
                        type="button"
                        data-pin-control
                        onClick={(e) => togglePin(e, message.id)}
                        className={`p-1.5 rounded-lg border transition-colors ${
                          isPinned
                            ? 'border-amber-300 bg-amber-100 text-amber-800 dark:border-amber-600 dark:bg-amber-900/50 dark:text-amber-200'
                            : 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/80 text-gray-600 dark:text-gray-300 hover:border-purple-300 dark:hover:border-purple-500'
                        }`}
                        title={isPinned ? t('unpinMessage') : t('pinMessage')}
                        aria-label={isPinned ? t('unpinMessage') : t('pinMessage')}
                      >
                        {isPinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                      </button>
                    </div>

                    <div className="flex-1 min-w-0 text-start">
                      <div className="flex items-center gap-2 mb-2 text-start">
                        {messageIsRead(message) ? (
                          <CheckCircle className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                        ) : (
                          <Circle className="w-4 h-4 text-purple-600 dark:text-purple-400 flex-shrink-0" />
                        )}
                        <h4
                          className={`text-sm truncate text-start w-full ${
                            messageIsRead(message)
                              ? 'font-normal text-gray-700 dark:text-gray-300'
                              : 'font-semibold text-gray-900 dark:text-white'
                          }`}
                        >
                          {resolveMessagePreview(message)}
                        </h4>
                      </div>
                      <div className="flex flex-col text-[11px] text-gray-500 dark:text-gray-400 mt-1 space-y-0.5 items-start text-start">
                        {getSenderDisplay(message) && (
                          <div className="flex items-center gap-1">
                            <User className="w-2.5 h-2.5 shrink-0" />
                            <span>{t('messages:from')} {getSenderDisplay(message)}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1">
                          <Clock className="w-2.5 h-2.5 shrink-0" />
                          <span>{t('messages:received')} {formatReceiveTime(message.sentAt)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Load More Button */}
          {messages.length < totalCount && (
            <div className="flex justify-center mt-6">
              <button
                onClick={loadMoreMessages}
                disabled={isLoadingMore}
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 dark:from-purple-500 dark:to-pink-500 dark:hover:from-purple-600 dark:hover:to-pink-600 text-white rounded-lg transition-all duration-200 flex items-center gap-2 font-medium shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-lg"
              >
                {isLoadingMore ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>{t('messages:loading')}</span>
                  </>
                ) : (
                  <>
                    <span>{t('loadMore')}</span>
                    <ChevronDown className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          )}
        </>
      )}
      </div>

      {/* Message detail — slide up like comments modal */}
      {messageModalMounted && typeof document !== 'undefined' && localizedDisplayMessage
        ? createPortal(
        <div className="learner-slide-modal-root fixed inset-0 z-[200] py-6 sm:py-10" role="presentation">
          <div
            className={`keyboard-slide-modal-backdrop fixed inset-0 bg-black/45 backdrop-blur-[2px] dark:bg-black/55${
              messageModalVisible ? ' is-visible' : ''
            }`}
            aria-hidden="true"
            onClick={handleCloseModal}
          />
          <div className="flex min-h-full items-center justify-center px-3 sm:px-6">
            <div
              ref={messageModalPanelRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby="message-detail-modal-title"
              className={`keyboard-slide-modal-panel keyboard-slide-modal-panel--slide-up pointer-events-auto relative flex max-h-[min(90vh,820px)] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border shadow-2xl message-modal-content ${
                isDark
                  ? ' border-gray-700 bg-gradient-to-br from-gray-800 to-gray-900'
                  : ' border-gray-200 bg-gradient-to-br from-white to-gray-50'
              }${messageModalVisible ? ' is-visible' : ''}`}
              dir={direction}
              onTransitionEnd={handleMessageModalExitTransitionEnd}
              onClick={(e) => e.stopPropagation()}
            >
            <div className={`shrink-0 border-b px-4 py-3 sm:px-5 sm:py-4 ${isDark ? 'border-gray-600 bg-gradient-to-r from-gray-700 to-gray-800' : 'border-gray-200 bg-gradient-to-r from-gray-50 to-white'}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`p-1.5 rounded-lg shrink-0 ${isDark ? 'bg-gray-700' : 'bg-purple-100'}`}>
                      <Mail className={`w-4 h-4 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                    </div>
                    <h3 id="message-detail-modal-title" className={`text-base sm:text-lg font-bold leading-tight truncate ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                      {localizedDisplayMessage.subject}
                    </h3>
                  </div>
                  <div className="flex flex-col gap-1 text-xs mt-0.5">
                    {getSenderDisplay(localizedDisplayMessage) && (
                      <div className={`flex items-center gap-1.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        <User className="w-3 h-3 shrink-0" />
                        <span>
                          <span className={isDark ? 'text-gray-500' : 'text-gray-500'}>{t('messages:from')} </span>
                          <span className="font-medium">{getSenderDisplay(localizedDisplayMessage)}</span>
                        </span>
                      </div>
                    )}
                    <div className={`flex items-center gap-1.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      <Clock className="w-3 h-3 shrink-0" />
                      <span>
                        <span className={isDark ? 'text-gray-500' : 'text-gray-500'}>{t('messages:received')} </span>
                        <span className="font-medium">{formatReceiveTime(localizedDisplayMessage.sentAt)}</span>
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleCloseModal}
                  className={`flex-shrink-0 p-1.5 rounded-lg transition-all duration-200 ${isDark ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
                  aria-label={t('common:close')}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className={`flex-1 overflow-y-auto overscroll-y-contain px-4 py-4 sm:px-6 sm:py-5 ${isDark ? 'bg-gray-900' : 'bg-white'}`}>
              <style>{`
                .message-content-html div {
                  color: #333;
                }
                .dark .message-content-html div {
                  color: #e5e7eb !important;
                }
                .message-content-html div[style*="background-color: #f9fafb"],
                .message-content-html div[style*="background-color:#f9fafb"] {
                  background-color: #f9fafb;
                }
                .dark .message-content-html div[style*="background-color: #f9fafb"],
                .dark .message-content-html div[style*="background-color:#f9fafb"] {
                  background-color: #1f2937 !important;
                }
                .message-content-html div[style*="border-top: 1px solid #e5e7eb"],
                .message-content-html div[style*="border-top:1px solid #e5e7eb"] {
                  border-top-color: #e5e7eb;
                }
                .dark .message-content-html div[style*="border-top: 1px solid #e5e7eb"],
                .dark .message-content-html div[style*="border-top:1px solid #e5e7eb"] {
                  border-top-color: #374151 !important;
                }
                .message-content-html span[style*="color: #6b7280"],
                .message-content-html span[style*="color:#6b7280"] {
                  color: #6b7280;
                }
                .dark .message-content-html span[style*="color: #6b7280"],
                .dark .message-content-html span[style*="color:#6b7280"] {
                  color: #9ca3af !important;
                }
                .message-content-html p[style*="color: #6b7280"],
                .message-content-html p[style*="color:#6b7280"] {
                  color: #6b7280;
                }
                .dark .message-content-html p[style*="color: #6b7280"],
                .dark .message-content-html p[style*="color:#6b7280"] {
                  color: #9ca3af !important;
                }
                .dark .message-content-html strong[style*="color: #10b981"],
                .dark .message-content-html strong[style*="color:#10b981"] {
                  color: #10b981 !important;
                }
                .dark .message-content-html strong[style*="color: #ef4444"],
                .dark .message-content-html strong[style*="color:#ef4444"] {
                  color: #ef4444 !important;
                }
                .dark .message-content-html [style*="color: #333"],
                .dark .message-content-html [style*="color:#333"] {
                  color: #e5e7eb !important;
                }
                .dark .message-content-html div[style*="background-color: #f0f9ff"],
                .dark .message-content-html div[style*="background-color:#f0f9ff"] {
                  background-color: rgba(30, 58, 95, 0.9) !important;
                  border-left-color: #60a5fa !important;
                  border-right-color: #60a5fa !important;
                  color: #e5e7eb !important;
                }
                .dark .message-content-html div[style*="background-color: #f0fdf4"],
                .dark .message-content-html div[style*="background-color:#f0fdf4"] {
                  background-color: rgba(6, 78, 59, 0.45) !important;
                  border-left-color: #22c55e !important;
                  border-right-color: #22c55e !important;
                  color: #d1fae5 !important;
                }
                .dark .message-content-html div[style*="background-color: #f0fdf4"] strong,
                .dark .message-content-html div[style*="background-color:#f0fdf4"] strong,
                .dark .message-content-html div[style*="background-color: #f0f9ff"] strong,
                .dark .message-content-html div[style*="background-color:#f0f9ff"] strong {
                  color: #f3f4f6 !important;
                }
                .dark .message-content-html strong {
                  color: #f3f4f6;
                }
                [dir="rtl"] .message-content-html,
                [dir="rtl"] .message-content-html * {
                  font-family: var(--font-persian) !important;
                  text-align: right;
                }
              `}</style>
              <div className="prose prose-gray dark:prose-invert max-w-none prose-sm">
                <div
                  className={`message-content-html text-gray-700 dark:text-gray-200 leading-relaxed text-sm font-normal ${isRtl ? 'text-end font-persian' : ''}`}
                  dangerouslySetInnerHTML={{ __html: localizedDisplayMessage.content }}
                  style={{ lineHeight: '1.6' }}
                />
              </div>
            </div>

            <div className={`shrink-0 border-t px-4 py-3 sm:px-6 ${isDark ? 'border-gray-700 bg-gray-900/80' : 'border-gray-200 bg-gray-50'}`}>
              <div className="flex items-center gap-3 justify-end">
                <button
                  onClick={handleCloseModal}
                  className={`px-5 py-2 rounded-lg transition-all font-medium text-sm shadow-sm hover:shadow-md ${isDark ? 'bg-white text-gray-900 hover:bg-gray-100' : 'bg-gray-900 text-white hover:bg-gray-800'}`}
                >
                  {t('common:close')}
                </button>
              </div>
            </div>
            </div>
          </div>
        </div>,
        document.body
      ) : null}
    </div>
  );
};

export default Inbox;
